import type { User } from "firebase/auth";

export type ManagedPaymentMethod = "stripe" | "ves";

export interface PlatformPaymentMethods {
  managed: ManagedPaymentMethod[];
  enabled: ManagedPaymentMethod[];
}

const getBackendBaseUrl = (): string => {
  const raw = process.env.REACT_APP_BACKEND_URL;
  if (!raw) {
    throw new Error("Missing REACT_APP_BACKEND_URL environment variable.");
  }
  return raw.replace(/\/+$/, "");
};

const parseError = async (response: Response): Promise<string> => {
  const data = (await response.json().catch(() => null)) as unknown;
  if (data && typeof data === "object" && "detail" in data) {
    const detail = (data as { detail: unknown }).detail;
    if (typeof detail === "string") return detail;
    if (detail && typeof detail === "object" && "message" in detail) {
      return String((detail as { message: unknown }).message);
    }
  }
  return `Backend error (${response.status}).`;
};

const mapPayload = (data: PlatformPaymentMethods): PlatformPaymentMethods => {
  const managed = (Array.isArray(data.managed) ? data.managed : []).filter(
    (m): m is ManagedPaymentMethod => m === "stripe" || m === "ves"
  );
  const enabled = (Array.isArray(data.enabled) ? data.enabled : []).filter(
    (m): m is ManagedPaymentMethod => m === "stripe" || m === "ves"
  );
  return {
    managed: managed.length ? managed : ["stripe", "ves"],
    enabled: enabled.length ? enabled : ["stripe", "ves"],
  };
};

export const fetchPlatformPaymentMethods = async (
  user: User
): Promise<PlatformPaymentMethods> => {
  const token = await user.getIdToken();
  const response = await fetch(
    `${getBackendBaseUrl()}/api/v1/platform/payment-methods`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  if (!response.ok) throw new Error(await parseError(response));
  return mapPayload((await response.json()) as PlatformPaymentMethods);
};

export const updatePlatformPaymentMethods = async (
  user: User,
  enabled: ManagedPaymentMethod[]
): Promise<PlatformPaymentMethods> => {
  const token = await user.getIdToken();
  const response = await fetch(
    `${getBackendBaseUrl()}/api/v1/platform/payment-methods`,
    {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ enabled }),
    }
  );
  if (!response.ok) throw new Error(await parseError(response));
  return mapPayload((await response.json()) as PlatformPaymentMethods);
};
